import SlideManager from "../utils/slide-manager";
import pako from "pako";
import { BSON } from "../../libs/bsonfy";

/**
 * Enques animations into queue.
 * @param {Object} props Parameters.
 * @param {Object} props.data Asset data.
 * @param {Object} props.queue Asset queue.
 */
function enqueAnimations({ data, queue }) {
    Object.keys(data.animations || {}).forEach(key => {
        queue.addItem(2, { uuid: key, data: data.animations[key], type: "animation" }, params => loadAnimation(params));
    });
}

/**
 * Populates slide animation data when loading from asset library.
 * @param {Object} props Parameters.
 * @param {Object} props.data Project data.
 * @param {Object} props.additionalData Asset data that needs to be added to all slides.
 */
function populateAnimationData({ data, additionalData }) {
    let newAnimations;
    let newMaterialAnimations;

    if (additionalData.slide) {
        newAnimations = additionalData.slide.animation;

        if (additionalData.slide.materialAnimation) {
            newMaterialAnimations = additionalData.slide.materialAnimation;
        }
    } else {
        newAnimations = Object.keys(additionalData.assets.objects).reduce((accum, key) => {
            accum[key] = {
                position: data.assets.objects[key]["position"],
                rotation: data.assets.objects[key]["rotation"],
                scale: data.assets.objects[key]["scale"],
                visible: true
            };

            return accum;
        }, {});
    }

    for (let slideData of data.slides) {
        slideData.animation = Object.assign({}, slideData.animation, JSON.parse(JSON.stringify(newAnimations)));

        if (newMaterialAnimations) {
            slideData.materialAnimation = Object.assign({}, slideData.materialAnimation || {}, JSON.parse(JSON.stringify(newMaterialAnimations)));
        }
    }

}

/**
 * Adds animation to queue.
 * @param {Object} props Parameters.
 * @param {Object} props.data Project data.
 * @param {Object} props.queue Animation queue.
 * @param {string} props.key Animation UUID.
 */
function collectAnimations({ data, queue, key }) {
    if (!data.assets.animations || !data.assets.animations[key]) return;
    const animation = data.assets.animations[key];

    queue.addItem(2, { uuid: key, data: animation, type: "animation" }, params => loadAnimation(params));
}

const loadingAnimations = {};

/**
 * Loads animation data.
 * @param {Object} props Parameters.
 * @param {Object} props.loaders Loaders.
 * @param {Object} props.assets Assets.
 * @param {Object} props.item Animation data.
 * @param {Object} props.data Project data.
 * @param {THREE.AnimationMixer} props.mixer Animation mixer.
 * @param {number} props.slide Actice slide.
 * @return {Promise}
 */
async function loadAnimation({ loaders, assets, item, data, mixer, slide, animationsWaiting }) {
    const isBSON = item.data.path.match(/\.bson$/i);
    const isZBSON = item.data.path.match(/\.zbson$/i);

    let promiseLoadAnimation;

    function playAnimation() {
        const slideData = SlideManager.getSlide(slide);

        Object.keys(data.assets.objects).forEach(key => {
            const animation = (slideData.animation[key] && slideData.animation[key].animation) ||
                (Q3.data.assets.objects[key] && Q3.data.assets.objects[key].animation);

            if (animation && animation.uuid === item.uuid) {
                animationsWaiting.push(function () {
                    mixer.playObjectAnimation({
                        objectUuid: key,
                        animationUuid: animation.uuid,
                        loopType: animation.type,
                        timescale: animation.timescale || 1,
                    });
                });
            }
        });
    }

    function downloadAnimation() {
        return new Promise((resolve, reject) => {
            function success(anim) {
                anim = !(isBSON || isZBSON)
                    ? JSON.parse(anim)
                    : isBSON
                        ? BSON.deserialize(new Uint8Array(anim))
                        : BSON.deserialize(new Uint8Array(pako.inflate(anim).buffer));

                const clip = assets.animations[item.uuid] = THREE.AnimationClip.parse(anim);

                delete loadingAnimations[item.uuid];

                playAnimation();

                resolve(clip);
            }

            const loader = isBSON || isZBSON ? loaders.binary : loaders.file;

            loader.load(Q3.s3Url + item.data.path, success, undefined, reject);
        });
    }

    if (loadingAnimations[item.uuid]) promiseLoadAnimation = loadingAnimations[item.uuid];
    else if (item.data.sourceAnimation && loadingAnimations[item.data.sourceAnimation]) {
        const clip = await loadingAnimations[item.data.sourceAnimation];
        assets.animations[item.uuid] = clip;

        playAnimation();

        return clip;
    } else if (item.data.sourceAnimation && assets.animations[item.data.sourceAnimation]) {
        const clip = assets.animations[item.uuid] = assets.animations[item.data.sourceAnimation];

        playAnimation();

        return clip;
    } else if (item.uuid in assets.animations) {
        const clip = assets.animations[item.uuid];

        playAnimation();

        return clip;
    } else {
        promiseLoadAnimation = downloadAnimation();
        loadingAnimations[item.uuid] = promiseLoadAnimation;
    }

    return await promiseLoadAnimation;
}

export { enqueAnimations, populateAnimationData, collectAnimations, loadAnimation };